/**
 * Normalisierung gescannter Codes.
 *
 * Uebernommen aus dem vorhandenen Inventur-Werkzeug des Ortsverbands
 * (`app/inventur/codes.py`), wo das Verfahren an echten Etiketten erprobt ist.
 * Die Uebersetzung nach TypeScript aendert nichts an den Regeln -- sie sind
 * teuer erkauft und bleiben, wie sie sind.
 *
 * Der Kern des Problems: Was im QR- oder Barcode steht, wissen wir nicht
 * sicher. Mal die nackte Inventarnummer, mal ohne Bindestrich, mal als URL,
 * mal mit Vorspann eines fremden Etikettendruckers. Statt auf ein Format zu
 * wetten, wird jeder Code auf vergleichbare Formen reduziert und werden alle
 * geprueft.
 *
 * Diese Funktionen laufen auch im Browser -- ein Scan muss offline zugeordnet
 * werden koennen, sonst waere der Scanner im Einsatz nutzlos.
 */
/** Laenge einer THW-Inventarnummer ohne Bindestrich: vier plus sechs Stellen. */
export const INVENTARNUMMER_STELLEN = 10;
const NICHT_ZIFFER = /\D+/g;
const NICHT_ALNUM = /[^A-Z0-9]+/g;
export function nurZiffern(text) {
    return text.replace(NICHT_ZIFFER, "");
}
export function nurAlnum(text) {
    return text.toUpperCase().replace(NICHT_ALNUM, "");
}
/**
 * Zerlegt einen Rohcode in die Bestandteile, die eine Nummer sein koennen.
 *
 * Steckt die Nummer in einer URL, ist sie fast immer das letzte Segment --
 * `https://host/l/0141900639` oder `...?nr=0141900639`.
 */
export function kandidaten(code) {
    const roh = code.trim();
    if (roh === "")
        return [];
    const teile = [roh];
    const ohneAnhang = roh.split("?")[0]?.split("#")[0]?.replace(/\/+$/, "") ?? "";
    for (const trenner of ["/", "="]) {
        if (ohneAnhang.includes(trenner)) {
            const letztes = ohneAnhang.split(trenner).at(-1);
            if (letztes !== undefined && letztes !== "")
                teile.push(letztes);
        }
    }
    return [...new Set(teile)].filter((t) => t !== "");
}
/** Liefert die Formen, gegen die in der Datenbank gesucht wird. */
export function suchSchluessel(code) {
    const ziffern = [];
    const alnum = [];
    for (const teil of kandidaten(code)) {
        const z = nurZiffern(teil);
        if (z.length >= 6) {
            ziffern.push(z);
            // Steht die Nummer in einer laengeren Zeichenkette -- etwa mit einem
            // Vorspann des Etikettendruckers --, zaehlen die letzten zehn Stellen.
            if (z.length > INVENTARNUMMER_STELLEN)
                ziffern.push(z.slice(-INVENTARNUMMER_STELLEN));
        }
        const a = nurAlnum(teil);
        if (a.length >= 4)
            alnum.push(a);
    }
    return { ziffern: [...new Set(ziffern)], alnum: [...new Set(alnum)] };
}
/**
 * Bringt einen Code auf die Schreibweise `0141-900639`, falls moeglich.
 *
 * Wird gebraucht, wenn ein unbekannter Code nacherfasst werden soll: Der
 * Vorschlag soll so aussehen wie eine Inventarnummer und nicht wie das, was
 * zufaellig im Etikett stand.
 */
export function formatiereInventarnummer(code) {
    for (const teil of kandidaten(code)) {
        const z = nurZiffern(teil);
        if (z.length === INVENTARNUMMER_STELLEN)
            return `${z.slice(0, 4)}-${z.slice(4)}`;
        if (z.length > INVENTARNUMMER_STELLEN) {
            const rest = z.slice(-INVENTARNUMMER_STELLEN);
            return `${rest.slice(0, 4)}-${rest.slice(4)}`;
        }
    }
    return null;
}
//# sourceMappingURL=codes.js.map